import { useState, type CSSProperties } from 'react'
import Link from 'src/components/Link'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import LaptopOutlinedIcon from '@mui/icons-material/LaptopOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import type { tItems } from 'src/shared/frontendTypes/wunschliste'
import type { tProductPreview } from 'src/shared/frontendTypes/product'
import WishlistCard from './WishlistCard'
import type { WishlistCartActions } from './useWishlistCart'
import styles from './ModernWishlist.module.css'

export interface ModernWishlistProps {
  shop: 101 | 202
  items: tItems
  products: tProductPreview[]
  loading: boolean
  error: boolean
  onRemove: (key: string) => void
  onRetry: () => void
  cart?: WishlistCartActions
}

export default function ModernWishlist({ shop, items, products, loading, error, onRemove, onRetry, cart }: ModernWishlistProps) {
  const [announcement, setAnnouncement] = useState('')
  const entries = Object.entries(items)
  const count = entries.length
  const name = shop === 101 ? 'HomeyLove' : 'LiebesZuhause'
  const theme = shop === 202 ? {
    '--wishlist-ink': '#1f1a17', '--wishlist-muted': '#6f655e', '--wishlist-accent': '#ba0063',
    '--wishlist-soft': '#f8eef3', '--wishlist-gradient': 'linear-gradient(115deg,#f8eaf2,#f3f0ed)',
  } as CSSProperties : undefined
  const remove = (key: string, title: string) => {
    onRemove(key)
    setAnnouncement(`${title} wurde aus Ihrer Wunschliste entfernt.`)
  }

  return <div className={styles.page} style={theme} data-wishlist-variant='modern'>
    <header className={styles.welcome}>
      <div>
        <p className={styles.eyebrow}>Mein {name}</p>
        <h1>Meine Wunschliste</h1>
        <p className={styles.intro}>Ihre Lieblingsstücke zum Wiederfinden und Weiterstöbern.</p>
      </div>
      <span className={styles.count}><FavoriteBorderIcon aria-hidden='true' fontSize='small' />
        {count} {count === 1 ? 'Lieblingsstück' : 'Lieblingsstücke'}
      </span>
    </header>

    <aside className={styles.storage} aria-label='Speicherung Ihrer Wunschliste'>
      <LaptopOutlinedIcon aria-hidden='true' fontSize='small' />
      <p>In diesem Browser gespeichert. Ihre Wunschliste wird nicht mit anderen Geräten synchronisiert.</p>
    </aside>
    <p className={styles.visuallyHidden} role='status' aria-live='polite' aria-atomic='true'>{announcement}</p>

    {count === 0 ? <section className={styles.empty} aria-labelledby='wishlist-empty-title'>
      <span className={styles.emptyIcon} aria-hidden='true'><FavoriteBorderIcon /></span>
      <p className={styles.eyebrow}>Ihre Sammlung beginnt hier</p>
      <h2 id='wishlist-empty-title'>Platz für Ihre Lieblingsstücke.</h2>
      <p>Entdecken Sie schöne Dinge für Ihr Zuhause. Mit dem Herz am Produkt merken Sie sich alles, was Ihnen gefällt.</p>
      <Link href='/' className={styles.primary}>Lieblingsstücke entdecken <ArrowForwardIcon fontSize='small' aria-hidden='true' /></Link>
      <Link href='/kundenkonto' className={styles.textLink}>Zum Kundenkonto</Link>
    </section> : <>
      <div className={styles.listHeading}>
        <h2>Für später gemerkt</h2>
        <Link href='/' className={styles.textLink}>Weiter entdecken <ArrowForwardIcon fontSize='small' aria-hidden='true' /></Link>
      </div>
      {error && <div className={styles.error} role='alert'>
        <div><strong>Die Produktinformationen konnten nicht aktualisiert werden.</strong><p>Ihre gemerkten Artikel bleiben in der Liste. Bitte versuchen Sie es noch einmal.</p></div>
        <button type='button' className={styles.retry} onClick={onRetry}><RefreshIcon fontSize='small' aria-hidden='true' /> Erneut laden</button>
      </div>}
      {loading && <p className={styles.loading} role='status'>Ihre Lieblingsstücke werden aktualisiert …</p>}
      <ul className={styles.grid} aria-label='Gemerkte Artikel' aria-busy={loading}>
        {entries.map(([key, item]) => {
          const product = products.find(candidate => candidate.key === key)
          return <li key={key}>{product
            ? <WishlistCard item={item} product={product} onRemove={() => remove(key, product.metaTitle || product.name)}
              cartState={cart?.states[key]} busy={Boolean(cart?.busy)} adding={cart?.busy === key}
              onAdd={cart ? () => void cart.add(product) : undefined} />
            : <article className={[styles.card, styles.unavailable].join(' ')}>
              <span className={styles.placeholderIcon} aria-hidden='true'><FavoriteBorderIcon /></span>
              <h3>Gespeicherter Artikel</h3>
              <p>{loading ? 'Die Produktinformationen werden geladen.' : 'Für diesen Artikel sind gerade keine Produktinformationen verfügbar.'}</p>
              <div className={styles.missingActions}>
                {!loading && <button type='button' className={styles.retry} onClick={onRetry}><RefreshIcon fontSize='small' aria-hidden='true' /> Erneut laden</button>}
                <button type='button' className={styles.remove} onClick={() => remove(key, 'Der Artikel')} aria-label={`Artikel ${item.id} aus der Wunschliste entfernen`}><DeleteOutlineIcon fontSize='small' aria-hidden='true' /> Entfernen</button>
              </div>
            </article>}
          </li>
        })}
      </ul>
      <p className={styles.productHint}>Öffnen Sie ein Produkt, um alle Details und die gewählte Variante anzusehen.</p>
    </>}
  </div>
}
